import React, {useCallback} from 'react';

import {observer} from 'mobx-react';
import {Button, CircularProgress} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import {useStores} from '../../../Hooks';
import Utility from '../../../Utility';
import GeneralDialog from '../GeneralDialog';
import TabSwitcher from './TabSwitcher';

const deleteButtonClass = '!normal-case !rounded-button !text-leavedeletenew-normal !border-leavedeletenew-normal';
const saveButtonClass = '!normal-case !rounded-button !bg-classicbutton-normal !text-classicbutton-normal-text !py-1.5 !px-6';

const SHELL_STYLES = `
.settings-input-wrap {
    border: 1px solid var(--panels-normal-color);
    border-radius: 8px;
    padding: 8px 12px;
    width: 100%;
    box-sizing: border-box;
    transition: border-color 0.15s;
}
.settings-input-wrap:focus-within {
    border-color: var(--classicbutton-normal-color);
    border-width: 2px;
    padding: 7px 11px;
}
.settings-section {
    padding: 16px 20px;
    display: flex;
    flex-direction: column;
    gap: 16px;
}
.settings-section + .settings-section {
    border-top: 1px solid var(--panels-normal-color);
}
`;

const ModalFooter = ({onSave, onDelete, isDirty, t}) => (
    <div className="flex items-center justify-between px-5 py-3 border-t border-panels-normal">
        {onDelete ? (
            <Button variant="outlined" size="small" startIcon={<DeleteOutlineIcon />} onClick={onDelete} className={deleteButtonClass} data-testid="settings-delete-button">
                {t('delete')}
            </Button>
        ) : <div />}
        <Button variant="contained" size="small" onClick={onSave} disabled={!isDirty} className={saveButtonClass} data-testid="settings-save-button">
            {t('save')}
        </Button>
    </div>
);

const SettingsModalShell = observer(({
    openControl, title, subtitle, tabs, activeTab, onTabChange,
    onEnter, isDirty, onSave, onDiscard, onDelete, closeModal, children, compact, isContentReady = true,
}) => {
    const {modalStore, generalStore} = useStores();
    const t = generalStore.translate.bind(generalStore);
    const isMobile = Utility.isMobile();

    const handleSaveAndClose = useCallback(async () => {
        await onSave();
        closeModal();
    }, [onSave, closeModal]);

    const handleClose = useCallback(() => {
        if (!isDirty) return closeModal();
        modalStore.modalData = {
            ...modalStore.modalData,
            title: t('unsavedChanges'),
            bodyText: t('youHaveUnsavedChanges'),
            confirmText: t('save'),
            cancelText: t('discard'),
            confirmActions: handleSaveAndClose,
            extraCloseActions: () => { onDiscard(); closeModal(); },
            persistModalData: true,
        };
        modalStore.isGeneralConfirmActionDialogOpen = true;
    }, [isDirty, handleSaveAndClose, onDiscard, closeModal, modalStore, t]);

    const dialogStyle = isMobile
        ? {width: '100%', height: '100%', maxWidth: '100%', maxHeight: '100%', margin: 0, borderRadius: 0}
        : compact ? {width: 520, maxWidth: 520} : {width: 520, maxWidth: 520, minHeight: '80%'};

    return (
        <GeneralDialog
            openControl={openControl}
            className="procedure-option-dialog settings special-scroll"
            specialOnClose={handleClose}
            onEnter={onEnter}
            title={null}
            body={
                <div className="w-full">
                    <style>{SHELL_STYLES}</style>
                    <div className="px-5 pt-4 border-b border-panels-normal">
                        <h2 className="text-lg font-semibold text-main-normal m-0">{title}</h2>
                        <p className="text-md text-main-hover mt-0.5 mb-3">{subtitle}</p>
                        {tabs && tabs.length > 1 && <TabSwitcher tabs={tabs} activeTab={activeTab} onTabChange={onTabChange} />}
                    </div>
                    {isContentReady ? children : (
                        <div className="flex items-center justify-center" style={{minHeight: 300}}>
                            <CircularProgress size={28} />
                        </div>
                    )}
                </div>
            }
            isConfirmDisabled={true}
            onConfirm={null}
            dialogFooter={<ModalFooter onSave={handleSaveAndClose} onDelete={onDelete} isDirty={isDirty} t={t} />}
            extraClose
            dialogStyle={dialogStyle}
        />
    );
});

export default SettingsModalShell;
